import * as React from 'react';
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import qs from 'qs';

import {
  ICartItems,
  IOrderItem,
  IInsuranceOrderItem,
  ICurrency,
  IJRWTicketOrderItem,
  ITWPTicketOrderItem,
  IJEPTicketOrderItem
} from './types';
import { CURRENT_CART_VERSION } from './constants';
import { ITEM_ADDED_TO_CART } from './events';
import {
  formatCartItems,
  countCartItems,
  retrieveCartItems,
  storeCartItems,
  returnTotalAmount,
} from './helpers';
import { getLocale } from '../utils/pathHelper';
import * as localStorage from '../utils/localStorage';
import { colors } from '../styles/variables';
import t from './locales';

import Button from '../Shared/Button';
import Price from '../Shared/Price';
import YadoOrderItem from './YadoOrderItem';
import OrderItem from './OrderItem';
import JRWTicketOrderItem from './JRWTicketOrderItem';
import TWPTicketOrderItem from './TWPTicketOrderItem';
import JEPTicketOrderItem from './JEPTicketOrderItem';
import CartUpSell from './CartUpSell';
import CouponTrigger from './CouponTrigger';
import CouponNotice from './CouponNotice';
import CartCheckoutView from './CartCheckoutView';
import CartContainerView from './CartContainerView';

interface ICartProps {
  localCurrencyExchangeRate?: number;
  localCurrency?: ICurrency;
  newCartItems?: any;
  isItemChanged?: boolean
}

const CartEmptyView = styled.div`
  padding: 32px 16px;
  text-align: center;
  border-radius: 4px;
  background-color: ${colors.gray200};
  color: ${colors.gray600};
`;

const PAYMENT_PAGE_URL = '/snow/me/payment/new';

const Cart: React.FC<ICartProps> = (props) => {
  const [cartItems, setCartItems] = useState<ICartItems>(formatCartItems({}));
  const [isLoading, setLoading] = useState(false);

  function updateCartItems(event: CustomEvent): void {
    setCartItems(event.detail.cartItems);
  }

  function initCartItems(props: ICartProps) {
    if (localStorage.get('userPutItemsToCartWithoutLoggingIn') === 'true') {
      storeCartItems(retrieveCartItems(CURRENT_CART_VERSION));
      setCartItems(retrieveCartItems(CURRENT_CART_VERSION));
      localStorage.remove('userPutItemsToCartWithoutLoggingIn');
      return;
    }

    // shopping cartのitem価格更新したら、localStorageとデータベースも更新します。
    if (Object.entries(props.newCartItems).length != 0) {
      props.isItemChanged &&
        storeCartItems(formatCartItems(props.newCartItems));
      setCartItems(formatCartItems(props.newCartItems));
    } else {
      setCartItems(retrieveCartItems(CURRENT_CART_VERSION));
    }
  }

  function handleNumberChange(event: React.ChangeEvent): void {
    const itemsType = event.currentTarget.getAttribute('data-items-type');
    const propKey: string = event.currentTarget.getAttribute('data-prop-key');
    const index: number = Number(
      event.currentTarget.getAttribute('data-index')
    );
    const value: number = Number((event.currentTarget as any).selectedIndex);
    const prevItems: Array<
      IOrderItem | IInsuranceOrderItem | IJRWTicketOrderItem | ITWPTicketOrderItem | IJEPTicketOrderItem
    > = cartItems[itemsType];
    const newItem:
      | IOrderItem
      | IInsuranceOrderItem
      | IJRWTicketOrderItem
      | ITWPTicketOrderItem
      | IJEPTicketOrderItem = { ...prevItems[index] }; // copy
    const newItems: Array<
      IOrderItem | IInsuranceOrderItem | IJRWTicketOrderItem | ITWPTicketOrderItem | IJEPTicketOrderItem
    > = prevItems.slice(); // copy
    if ('numPassengers' in newItem) {
      newItem.numPassengers = value;
      if (newItem.numPassengers === 0) {
        newItem.numPassengers = 1;
      }
    } else if ('adultQuantity' in newItem) {
      newItem.adultQuantity = value
    } else if ('people' in newItem) {
      newItem.people[propKey] = value;
      if (
        newItem.people.adultNum +
          newItem.people.childNum +
          newItem.people.studentNum +
          newItem.people.seniorNum ===
        0
      ) {
        newItem.people[propKey] = 1;
      }
    }
    if ('maxParticipants' in newItem) {
      if (
        newItem.maxParticipants &&
        newItem.people.adultNum +
          newItem.people.childNum +
          newItem.people.studentNum +
          newItem.people.seniorNum >
          newItem.maxParticipants
      ) {
        alert(
          t.pleaseSelectValidNumberOfParticipants({
            max: newItem.maxParticipants,
          })
        );
        return;
      }
    }
    newItems.splice(index, 1, newItem);
    const newCartItems: ICartItems = { ...cartItems, [itemsType]: newItems };
    setCartItems(newCartItems);
    storeCartItems(newCartItems);
  }

  function handleRemove(event: React.MouseEvent): void {
    event.preventDefault();
    if (confirm(t.areYouSureToRemoveThisItemFromCart())) {
      const itemsType = event.currentTarget.getAttribute('data-items-type');
      const index: number = Number(
        event.currentTarget.getAttribute('data-index')
      );
      const prevItems: Array<
        | IOrderItem
        | IInsuranceOrderItem
        | IJRWTicketOrderItem
        | ITWPTicketOrderItem
        | IJEPTicketOrderItem
      > = cartItems[itemsType];
      const newItems: Array<
        | IOrderItem
        | IInsuranceOrderItem
        | IJRWTicketOrderItem
        | ITWPTicketOrderItem
        | IJEPTicketOrderItem
      > = prevItems.slice(); // copy
      newItems.splice(index, 1);
      const newCartItems: ICartItems = { ...cartItems, [itemsType]: newItems };
      setCartItems(newCartItems);
      storeCartItems(newCartItems);
    }
  }

  useEffect(() => {
    initCartItems(props);

    document.addEventListener(ITEM_ADDED_TO_CART, updateCartItems);
    return () => {
      document.removeEventListener(ITEM_ADDED_TO_CART, updateCartItems);
    };
  }, []);

  const userCouponId = qs.parse(
    window.location.search.replace('?', '')
  ).user_coupon_id;
  const totalPrice = returnTotalAmount(cartItems);
  const count = countCartItems(cartItems);
  const {
    yadoOrderItems,
    orderItems,
    insuranceOrderItems,
    jrwTicketOrderItems,
    twpTicketOrderItems,
    jepTicketOrderItems
  } = cartItems;
  const deposit = cartItems.orderItems.reduce(
    (result, item) =>
      result +
      item.deposit *
        (item.people.adultNum +
          item.people.childNum +
          item.people.studentNum +
          item.people.seniorNum),
    0
  );
  return (
    <CartContainerView>
      <div>
        {count === 0 && <CartEmptyView>{t.cartIsEmpty()}</CartEmptyView>}
        {yadoOrderItems &&
          yadoOrderItems.map((item, index) => (
            <YadoOrderItem
              item={item}
              itemsType='yadoOrderItems'
              handleChange={handleNumberChange}
              handleRemove={handleRemove}
              index={index}
              key={`yadoOrderItem-${index}`}
              localCurrency={props.localCurrency}
            />
          ))}
        {orderItems &&
          orderItems.map((item, index) => (
            <OrderItem
              item={item}
              itemsType='orderItems'
              handleChange={handleNumberChange}
              handleRemove={handleRemove}
              index={index}
              key={`orderItem-${index}`}
              localCurrency={props.localCurrency}
              charge={item.isCharge}
              axess={item.forAxess}
            />
          ))}
        {insuranceOrderItems &&
          insuranceOrderItems.map((item, index) => (
            <OrderItem
              item={item}
              itemsType='insuranceOrderItems'
              handleChange={handleNumberChange}
              handleRemove={handleRemove}
              index={index}
              key={`insuranceOrderItem-${index}`}
              localCurrency={props.localCurrency}
              charge={false}
            />
          ))}
        {jrwTicketOrderItems &&
          jrwTicketOrderItems.map((item, index) => (
            <JRWTicketOrderItem
              item={item}
              itemsType='jrwTicketOrderItems'
              handleChange={handleNumberChange}
              handleRemove={handleRemove}
              index={index}
              key={`jrwTicketOrderItems-${index}`}
              localCurrency={props.localCurrency}
            />
          ))}
        {twpTicketOrderItems &&
          twpTicketOrderItems.map((item, index) => (
            <TWPTicketOrderItem
              item={item}
              itemsType='twpTicketOrderItems'
              handleChange={handleNumberChange}
              handleRemove={handleRemove}
              index={index}
              key={`twpTicketOrderItems-${index}`}
              localCurrency={props.localCurrency}
            />
          ))}
        {jepTicketOrderItems &&
          jepTicketOrderItems.map((item, index) => (
            <JEPTicketOrderItem
              item={item}
              itemsType='jepTicketOrderItems'
              handleChange={handleNumberChange}
              handleRemove={handleRemove}
              index={index}
              key={`jepTicketOrderItems-${index}`}
              localCurrency={props.localCurrency}
            />
          ))}
        {insuranceOrderItems.length === 0 && <CartUpSell />}
      </div>
      <CartCheckoutView>
        <div>
          <Price
            isStrikeThrough={!!userCouponId}
            price={totalPrice.price}
            fullPrice={totalPrice.fullPrice}
            showDiscount
            style={{ textAlign: 'center' }}
            fontSize={18}
            localCurrency={props.localCurrency}
          />
          <CouponNotice isSelected={!!userCouponId} />
          {deposit > 0 && (
            <div className='d-flex align-items-center justify-content-center mt-2'>
              <span className='small text-muted mr-2'>
                {t.icCardDeposit()}:
              </span>
              <Price
                price={deposit}
                fontSize={12}
                localCurrency={props.localCurrency}
              />
            </div>
          )}
        </div>
        <div className='footer'>
          <CouponTrigger isSelected={!!userCouponId} />
          <Button
            href={
              userCouponId
                ? `${PAYMENT_PAGE_URL}?user_coupon_id=${userCouponId}`
                : PAYMENT_PAGE_URL
            }
            primary
            isLoading={isLoading}
            onClick={() => {
              setLoading(true);
            }}
          >
            {t.checkout()}
          </Button>
        </div>

        <p className='note'>
          {getLocale() == 'ja' ? '' : t.paymentIsInJapaneseYen()}
          {/* 価格は2019年12月1日 00:00時点の為替で計算した参考価格です。 */}
        </p>
      </CartCheckoutView>
    </CartContainerView>
  );
};

export default Cart;
